import { cleanText, sanitizePath, sanitizeQuery } from "../lib/sanitize";
import type { ActivityType, PageRef } from "./types";

export type PageViewReason = "load" | "route" | "bfcache" | "rotate";
export type PageLeaveReason = "route" | "tab_closed" | "rotate";

export interface PagesApi {
    track: (type: ActivityType, data: Record<string, unknown>, page: PageRef) => void;
    routeOf: (pathname: string) => string;
}

interface OpenPage {
    rawPath: string;
    ref: PageRef;
    startedMs: number;
    visibleMs: number;
    visibleSince: number | null;
    maxScrollPct: number | null;
    fitsScreen: boolean;
    clicks: number;
}

// Дробные пиксели и рамки дают scrollHeight чуть больше clientHeight и там,
// где прокручивать нечего.
const SCROLL_SLACK_PX = 4;

const scrolls = (el: Element): boolean => el.scrollHeight > el.clientHeight + SCROLL_SLACK_PX;

// Мелкие прокручиваемые блоки (списки в карточках, чаты) — не страница.
const pageSized = (el: Element): boolean => el.clientHeight >= window.innerHeight * 0.5;

// Блок, который ещё не прокручивали, ищем под серединой экрана. overflow
// проверяем: блок с overflow: visible тоже бывает выше своей высоты (в
// кабинете на телефоне так у .content), но прокручивает тогда его родитель.
const scrollerUnderCentre = (): Element | null => {
    if (typeof document.elementsFromPoint !== "function") return null;
    const hits = document.elementsFromPoint(window.innerWidth / 2, window.innerHeight / 2);
    return (
        hits.find(
            (el) =>
                scrolls(el) &&
                pageSized(el) &&
                /auto|scroll|overlay/.test(getComputedStyle(el).overflowY),
        ) ?? null
    );
};

/**
 * Просмотры страниц: сколько страница была открыта и видна, как глубоко её
 * прокрутили. В личном кабинете прокручивается не окно, а div, поэтому
 * прокрутку ловим в фазе захвата на document — scroll не всплывает — и
 * меряем самый большой блок, который правда прокручивается. У окна там
 * clientHeight равен scrollHeight, и мера по окну давала 100% без прокрутки.
 * Страница, где не прокручивается ничего, видна целиком (100%).
 */
export class PageTracker {
    private cur: OpenPage | null = null;
    private lastPath: string | null = null;
    private rafPending = false;
    private scroller: Element | null = null;
    private firstMeasure: ReturnType<typeof setTimeout> | null = null;
    lastRouteChangeMs = 0;

    constructor(
        private readonly api: PagesApi,
        private readonly now: () => number = Date.now,
    ) {}

    install(): () => void {
        const opts: AddEventListenerOptions = { capture: true, passive: true };
        document.addEventListener("scroll", this.onScroll, opts);
        return () => document.removeEventListener("scroll", this.onScroll, opts);
    }

    onRoute(pathname: string, search: string): void {
        // StrictMode повторяет эффект с тем же путём — это не переход.
        if (this.cur && this.cur.rawPath === pathname) return;
        const prev = this.cur;
        if (prev) this.leave("route");
        this.open(pathname, search, prev ? "route" : "load", prev?.ref.path ?? this.lastPath);
    }

    /** Открыть заново текущий адрес: новый сеанс журнала или возврат из bfcache. */
    reopen(reason: PageViewReason): void {
        if (this.cur) return;
        this.open(window.location.pathname, window.location.search, reason, this.lastPath);
    }

    leave(reason: PageLeaveReason): void {
        const c = this.cur;
        if (!c) return;
        this.cur = null;
        this.lastPath = c.ref.path;
        const now = this.now();
        const visible = c.visibleMs + (c.visibleSince !== null ? now - c.visibleSince : 0);
        this.api.track(
            "page_leave",
            {
                dwell_ms: now - c.startedMs,
                visible_ms: visible,
                max_scroll_pct: c.maxScrollPct ?? (c.fitsScreen ? 100 : null),
                clicks: c.clicks,
                reason,
                title_final: cleanText(document.title, 120) || undefined,
            },
            c.ref,
        );
    }

    onVisibility(hidden: boolean): void {
        const c = this.cur;
        if (!c) return;
        const now = this.now();
        if (hidden && c.visibleSince !== null) {
            c.visibleMs += now - c.visibleSince;
            c.visibleSince = null;
        } else if (!hidden && c.visibleSince === null) {
            c.visibleSince = now;
        }
    }

    noteClick(): void {
        if (this.cur) this.cur.clicks++;
    }

    currentRef(): PageRef | null {
        return this.cur ? this.cur.ref : null;
    }

    private open(
        pathname: string,
        search: string,
        reason: PageViewReason,
        prevPath: string | null | undefined,
    ): void {
        const now = this.now();
        const ref: PageRef = { path: sanitizePath(pathname), route: this.safeRoute(pathname) };
        this.cur = {
            rawPath: pathname,
            ref,
            startedMs: now,
            visibleMs: 0,
            visibleSince: document.hidden ? null : now,
            maxScrollPct: null,
            fitsScreen: false,
            clicks: 0,
        };
        this.lastRouteChangeMs = now;
        const title = cleanText(document.title, 120);
        this.api.track(
            "page_view",
            { query: sanitizeQuery(search), prev_path: prevPath || undefined, reason },
            title ? { ...ref, title } : ref,
        );
        // Без прокрутки видна первая часть страницы, а короткая — целиком: меряем, как она легла.
        if (this.firstMeasure) clearTimeout(this.firstMeasure);
        this.firstMeasure = setTimeout(() => {
            this.firstMeasure = null;
            this.measure();
        }, 1_500);
    }

    private safeRoute(pathname: string): string {
        try {
            return this.api.routeOf(pathname);
        } catch {
            return "";
        }
    }

    private onScroll = (e: Event): void => {
        this.noteScroller(e.target);
        if (this.rafPending) return;
        this.rafPending = true;
        requestAnimationFrame(() => {
            this.rafPending = false;
            this.measure();
        });
    };

    private noteScroller(target: EventTarget | null): void {
        const el = target === document || target === window ? document.scrollingElement : target;
        if (!(el instanceof Element) || !scrolls(el) || !pageSized(el)) return;
        const seen = this.liveScroller();
        if (!seen || el.clientHeight > seen.clientHeight) this.scroller = el;
    }

    /** Блок с прошлой страницы годится, пока он в документе и прокручивается. */
    private liveScroller(): Element | null {
        const s = this.scroller;
        if (s && s.isConnected && scrolls(s) && pageSized(s)) return s;
        this.scroller = null;
        return null;
    }

    private measure(): void {
        const c = this.cur;
        if (!c) return;
        // Если прокручивается само окно, страница — оно, а не диалог или панель поверх.
        const doc = document.scrollingElement;
        const docScrolls = !!doc && scrolls(doc);
        let seen = this.liveScroller();
        if (!seen && !docScrolls) seen = this.scroller = scrollerUnderCentre();
        const el =
            doc && docScrolls && (!seen || doc.clientHeight >= seen.clientHeight) ? doc : seen;
        if (!el) {
            // Первая мера бывает до загрузки данных: прокрутка, замеренная
            // позже, этот ответ перекрывает.
            c.fitsScreen = true;
            return;
        }
        const pct = Math.min(
            100,
            Math.round((100 * (el.scrollTop + el.clientHeight)) / el.scrollHeight),
        );
        if (c.maxScrollPct === null || pct > c.maxScrollPct) c.maxScrollPct = pct;
    }
}
